import { useCallback } from 'react';

/* eslint-disable import/order -- CI lints `-api` before `/` (correct ASCII), local eslint --fix reverses it; disable to keep CI happy. */
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
/* eslint-enable import/order */

import { useSpaceStoreActions } from '../../controllers/space-store/hooks';
import type { UpdateSpaceRequest, UpdateSpaceResponse } from '../../controllers/space-store/types';

import { updateSpaceFetchAction } from './useUpdateSpace.fetch';

type UseUpdateSpaceProps = {
	spaceId: string;
	cloudId: string;
	product: string;
	experienceId: string;
	onSuccess?: (space: UpdateSpaceResponse) => void;
	onError?: (error: Error) => void;
};

export const useUpdateSpace = ({
	spaceId,
	cloudId,
	product,
	experienceId,
	onSuccess,
	onError,
}: UseUpdateSpaceProps): {
	mutate: (
		payload: UpdateSpaceRequest,
		callbacks?:
			| {
					onSuccess?: ((data: UpdateSpaceResponse) => void) | undefined;
					onError?: (error: ApiError) => void;
			  }
			| undefined,
	) => Promise<UpdateSpaceResponse | undefined>;
	isSubmitting: boolean;
	error: ApiError | null;
} => {
	const { patchSpace } = useSpaceStoreActions();

	const mutationFn = useCallback(
		async (request: UpdateSpaceRequest): Promise<UpdateSpaceResponse> => {
			const response = await updateSpaceFetchAction({
				spaceId,
				request,
				cloudId,
				product,
				experienceId,
			});
			// Optimistically patch both list and detail slices so any consumer
			// (side-nav, manage modal, landing page) sees the new fields
			// immediately, without waiting for a list refetch.
			patchSpace(spaceId, {
				name: response.name,
				emoji: response.emoji ?? null,
				description: response.description ?? null,
				updatedAt: response.updatedAt,
			});
			return response;
		},
		[spaceId, cloudId, product, experienceId, patchSpace],
	);

	const { mutate, isSubmitting, error } = useMutation<UpdateSpaceRequest, UpdateSpaceResponse>({
		mutationFn,
		onSuccess,
		onError,
	});

	return { mutate, isSubmitting, error };
};
